<script lang="ts">
  export default {
    name: 'DocumentEditModal'
  };
</script>

<script setup lang="ts">
import { onMounted, ref, computed, watchEffect, watch } from 'vue';
  import { DocumentType, Document, UpdateDocumentRequest } from '@/types/v2/Document';
  import AppModal from '@/components/v2/AppModal.vue';
  import AppSelect from '@/components/v2/AppSelect.vue';
  import { IsUserAClient } from '@/utilities/AuthUtils';
  import Store from '@/store';
  import z, { SafeParseError, ZodFormattedError } from 'zod';
  import { documentService } from '@/services/v2/DocumentService.js';
  import Combobox, { type ComboboxOption } from '@/components/v2/Combobox.vue';
  import { X } from 'lucide-vue-next';

  // -----
  // props
  // -----

  const props = defineProps<{
    title: string;
    description?: string;
    policyDocumentTypes: DocumentType[];
    document?: Document;
    show: boolean;
    includeTags?: boolean;
  }>();

  // -----
  // data
  // -----

const showModal = ref<boolean>(false);

  const emit = defineEmits<{
    (e: 'update', value?: Partial<UpdateDocumentRequest>): void;
    (e: 'close'): void;
  }>();

  const formSchema = z.object({
    displayName: z.string().trim().min(1, { message: 'Display Name is required' }),
    documentTypeId: z.number()
  });

  type FormSchemeType = z.infer<typeof formSchema>;

  type ValidFormFields = {
    [K in keyof FormSchemeType]: boolean;
  };

  const defaultValidFormFields: ValidFormFields = {
    displayName: true,
    documentTypeId: true
  };

  class UpdateDocumentForm {
    displayName?: string;
    documentTypeId?: number;
    tags?: string[];
  }


  // -----
  // refs
  // -----

  const form = ref<UpdateDocumentForm>(new UpdateDocumentForm());
  const formErrors = ref<ZodFormattedError<FormSchemeType>>();
  const validFormFields = ref<ValidFormFields>(structuredClone(defaultValidFormFields));
  const appModal = ref<typeof AppModal>();
  const availableTags = ref<ComboboxOption[]>([]);
  const selectedTags = ref<ComboboxOption[]>([]);
  const selectedTag = ref<string>('');

  // -----
  // computed
  // -----

  const canEditDocumentType = computed(() => {
    return !(props.document?.documentTypeName === 'Binder' && props.document?.documentSourceName === 'Email')
      && !IsUserAClient(Store.state.user);
  });

  const availableTagsFiltered = computed(() => {
    return availableTags.value.filter(tag => 
      !selectedTags.value.some(selectedTag => selectedTag.value === tag.value)
    );
  });


  // -----
  // methods
  // -----

  function handleUpdate() {
    try {
      // validate the form
      if (!validateForm()) {
        console.error('Failed to update document');
        return;
      }

      const updateDocumentRequest = {
        ...form.value,
        tags: props.includeTags ? selectedTags.value.map(tag => tag.value) : undefined
      };

      emit('update', updateDocumentRequest);
    } catch (e) {
      console.error('Failed to update document', e);
    } finally {
      closeModal();
    }
  }

  function validateForm(): boolean {
    const valid = formSchema.safeParse(form.value);
    validFormFields.value = structuredClone(defaultValidFormFields);

    if (!valid.success) {
      // set valid form fields to false
      formErrors.value = valid.error.format();

      for (const error of (valid as SafeParseError<FormSchemeType>).error.issues) {
        validFormFields.value[error.path[0].toString() as keyof ValidFormFields] = false;
      }
    }

    return valid.success;
  }
  
  function openModal() {
    appModal.value?.open();
  }
  
  function closeModal() {
    appModal.value?.close();
    emit('close');
  }

  async function getTags() {
    try {
      const tags = await documentService.getDocumentTags();
      availableTags.value = (tags || []).map(tag => ({
        label: tag.name,
        value: tag.name
      }));
    } catch (error) {
      console.error('Failed to load tags:', error);
      availableTags.value = [];
    }
  }

  function handleTagSelect(tag: ComboboxOption) {
    if (!selectedTags.value.some(selectedTag => selectedTag.value === tag.value)) {
      selectedTags.value.push(tag);
    }
    // Clear the selection after adding
    selectedTag.value = '';
  }

  function handleTagRemove(tag: ComboboxOption) {
    selectedTags.value = selectedTags.value.filter(selectedTag => selectedTag.value !== tag.value);
  }

  // -----
  // lifecycles
  // -----

   onMounted(() => {
    showModal.value = props.show;

    if (props.document) {
      form.value.displayName = props.document.displayName;
      form.value.documentTypeId = props.policyDocumentTypes.find(t => t.id === props.document?.documentTypeId)?.id || props.policyDocumentTypes[0].id;
      
      // Initialize selected tags from document
      if (props.document.tags && Array.isArray(props.document.tags)) {
        selectedTags.value = props.document.tags.map(tag => ({
          label: tag,
          value: tag
        }));
      }
    }

    if (props.includeTags) {
      getTags();
    }
  });

  watchEffect(() => {
    if (appModal.value) {
      // stop the v-menu from closing when clicking inside the modal
      appModal.value.dialogRef.addEventListener('click', (e: any) => {
        e.stopPropagation();
      });
    }
  });

  watch(
      () => props.show,
      (newValue) => {
        showModal.value = newValue;
        if (showModal.value && props.document) {
          form.value.displayName = props.document.displayName;
          form.value.documentTypeId = props.policyDocumentTypes.find(t => t.id === props.document?.documentTypeId)?.id || props.policyDocumentTypes[0].id;
          
          // Initialize selected tags from document
          if (props.document.tags && Array.isArray(props.document.tags)) {
            selectedTags.value = props.document.tags.map(tag => ({
              label: tag,
              value: tag
            }));
          }
        }
      }
  );
</script>

<template>
  <slot name="activator" :open="openModal">
    <button class="tw:hidden">
      Edit
    </button>
  </slot>
  <AppModal
    :show="showModal"
    :loading="false"
    action-button-label="Save"
    modal-class="tw:max-w-md"
    :action-disabled="false"
    :handle-cancel="() => closeModal()"
    :handle-action="() => handleUpdate()"
    :show-overflow="true"
    ref="appModal"
  >
    <template v-slot:title>
      <h2 class="tw:font-normal tw:text-primary">{{ title }}</h2>
      <p class="tw:italic">{{ description ?? '' }}</p>
    </template>
    <template v-slot:content>
      <div class="tw:flex tw:flex-col tw:gap-4">

        <!-- Display Name -->
        <div class="tw:flex tw:flex-col tw:gap-1">
          <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">
                Display Name
              </span>
          </label>
          <input
            type="text"
            v-model="form.displayName"
            class="tw:input tw:border tw:border-solid tw:min-w-0 tw:w-full" />
          <span v-show="formErrors?.displayName?._errors"
                class="tw:label-text-alt tw:text-xs tw:text-red-500 tw:ml-0.5">
            {{ formErrors?.displayName?._errors.join(', ') }}
          </span>
          <span class="tw:label-text-alt tw:text-xs tw:text-gray-500 tw:ml-0.5">
            {{ 'Original: ' + document?.fileName }}
          </span>
        </div>

        <!-- Document Type-->
        <div v-if="canEditDocumentType">
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500">Document Type</span>
          </label>
          <AppSelect
            v-model="form.documentTypeId"
            :options="policyDocumentTypes.map(type => ({
              label: type.name,
              value: type.id
            }))"
            class="tw:w-full tw:h-12"
            :error="formErrors?.documentTypeId?._errors !== undefined"
            :disable-teleport="true"
          />
          <span v-show="formErrors?.documentTypeId?._errors"
                class="tw:label-text-alt tw:text-xs tw:text-red-500 tw:ml-0.5">
            {{ formErrors?.documentTypeId?._errors.join(', ') }}
          </span>
        </div>

        <!-- Tags -->
        <div v-if="props.includeTags">
          <div class="tw:flex tw:flex-col">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1">Tags</span>
            </label>

            <div v-if="selectedTags.length > 0" class="tw:flex tw:flex-wrap tw:pb-2">
              <div
                v-for="tag in selectedTags"
                :key="tag.value"
                @click="handleTagRemove(tag)"
                class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
                <div class="tw:p-1">{{ tag.label }}</div>
                <X :size="12" class="tw:group-hover:stroke-red-600" />
              </div>
            </div>

            <Combobox
              v-model="selectedTag"
              :options="availableTagsFiltered"
              placeholder="Add tags..."
              :allow-free-text="true"
              @selected="handleTagSelect" />
          </div>
        </div>

      </div>
    </template>
  </AppModal>
</template>

